import {
  BuyType,
  ControlBoardChannel,
  ControlBoardPlanLine,
  ControlBoardRow,
} from "../../types/controlBoard";

/**
 * Cálculos del tablero de control. Todo sale de las filas línea × mes que da el backend y de
 * las líneas del Business Plan activo; aquí se agregan por cliente, mes, canal, técnico o
 * account.
 *
 * Convenciones:
 * - Facturación = venta con margen (la inversión del cliente en paid media que paga él).
 * - Beneficio bruto = facturación − coste neto de plataforma.
 * - El margen en % se calcula solo sobre las líneas que llevan margen: el paid media que paga
 *   el cliente va a coste y lo diluiría.
 * - Margen del margen = beneficio real − facturación × margen vendido en el briefing: lo que la
 *   compra mejora o empeora lo que se vendió.
 */

export const MONTHS = [
  "",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
export const MONTHS_SHORT = [
  "",
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

/** Temporadas que mueven los costes. */
export const SEASONS: Record<number, string> = {
  1: "Rebajas",
  7: "Rebajas",
  11: "Black Friday",
  12: "Navidad",
};

export const CM_CHANNELS: ControlBoardChannel[] = [
  "Display",
  "Skin",
  "Twitch",
  "Native",
  "Rich Media",
  "Connected TV",
  "YouTube",
  "Audio",
  "DOOH",
];
export const PM_CHANNELS: ControlBoardChannel[] = ["Google Ads", "Social Ads"];
export const CHANNELS: ControlBoardChannel[] = [...CM_CHANNELS, ...PM_CHANNELS];
export const isPaidMedia = (channel: ControlBoardChannel) =>
  PM_CHANNELS.includes(channel);

/** Un color por canal: azules y violetas para compra de medios, grises claros para paid media. */
export const CHANNEL_COLORS: Record<ControlBoardChannel, string> = {
  Display: "#1E3A8A",
  Skin: "#1D4ED8",
  Twitch: "#6D28D9",
  Native: "#2563EB",
  "Rich Media": "#7C3AED",
  "Connected TV": "#0891B2",
  YouTube: "#DC2626",
  Audio: "#059669",
  DOOH: "#D97706",
  "Google Ads": "#9CA3AF",
  "Social Ads": "#D1D5DB",
};
export const CM_COLOR = "#1E3A8A";
export const PM_COLOR = "#CBD5E1";

// ──────────────────────────────────────────────────────────────────────
// Formato
// ──────────────────────────────────────────────────────────────────────

const euroFormat = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const euro2Format = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const euro3Format = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const finite = (value: number) => Number.isFinite(value);

export const eur = (value: number): string =>
  euroFormat.format(Math.round(finite(value) ? value : 0));

/** Con signo: +1.200 € / −300 €. */
export const eurSigned = (value: number): string => {
  if (!finite(value) || Math.round(value) === 0) return "0 €";
  return (value > 0 ? "+" : "−") + eur(Math.abs(value));
};

/** Compacto: 12 k€, 1,35 M€. */
export const keur = (value: number): string => {
  if (!finite(value)) return "—";
  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (abs >= 1e6)
    return `${sign}${(abs / 1e6).toLocaleString("es-ES", { maximumFractionDigits: 2 })} M€`;
  if (abs >= 1e3)
    return `${sign}${Math.round(abs / 1e3).toLocaleString("es-ES")} k€`;
  return `${sign}${Math.round(abs)} €`;
};

export const keurSigned = (value: number): string =>
  (value >= 0 ? "+" : "") + keur(value);

/** Precio o coste unitario: decimales según el tamaño. */
export const unitMoney = (value: number): string => {
  if (!finite(value) || value === 0) return "—";
  if (value >= 100) return eur(value);
  if (value >= 1) return euro2Format.format(value);
  return euro3Format.format(value);
};

/** 0,253 → "25,3 %". */
export const pct = (value: number, decimals = 1): string => {
  if (!finite(value)) return "—";
  const rounded = Number((value * 100).toFixed(decimals)) || 0;
  return `${rounded.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} %`;
};

/** Variación con signo: +5 % / −12 %. */
export const pctSigned = (value: number, decimals = 0): string =>
  finite(value) ? (value >= 0 ? "+" : "") + pct(value, decimals) : "—";

/** Puntos porcentuales con signo: +2,1 pp. */
export const pp = (value: number, decimals = 1): string => {
  if (!finite(value)) return "—";
  const abs = Math.abs(value) * 100;
  if (Number(abs.toFixed(decimals)) === 0)
    return `${(0).toFixed(decimals).replace(".", ",")} pp`;
  return `${value >= 0 ? "+" : "−"}${abs.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} pp`;
};

export const modelLabel = (objective: string): string =>
  objective === "CPM" ? "CPM (por mil)" : objective;

/** BUMPER_6S → "Bumper 6s". */
export const subformatLabel = (value: string | null): string => {
  if (!value) return "Sin subformato";
  const text = value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b(iaba|iab|ctv|ott|ooh|dooh)\b/g, (m) => m.toUpperCase())
    .replace(/(\d+)s\b/g, "$1s");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// ──────────────────────────────────────────────────────────────────────
// Semáforos
// ──────────────────────────────────────────────────────────────────────

export type Tone = "good" | "warn" | "bad" | "none";

/** Cumplimiento: verde desde el 98 %, ámbar desde el 90 %. */
export const toneOfCompliance = (value: number): Tone =>
  !finite(value)
    ? "none"
    : value >= 0.98
      ? "good"
      : value >= 0.9
        ? "warn"
        : "bad";

/** Diferencia de margen: verde si no baja, ámbar hasta −3 pp. */
export const toneOfMarginDiff = (value: number): Tone =>
  !finite(value)
    ? "none"
    : value >= -0.0005
      ? "good"
      : value >= -0.03
        ? "warn"
        : "bad";

/** Importe: verde si no resta, ámbar si resta menos del 5 % de la referencia. */
export const toneOfAmount = (value: number, reference: number): Tone =>
  !finite(value)
    ? "none"
    : value >= -0.5
      ? "good"
      : value >= -0.05 * Math.abs(reference || 1)
        ? "warn"
        : "bad";

/** Ejecución de paid media: bien entre el 98 % y el 105 %, pasarse es peor que quedarse corto. */
export const toneOfExecution = (value: number): Tone =>
  !finite(value)
    ? "none"
    : value >= 0.98 && value <= 1.05
      ? "good"
      : value >= 0.9 && value <= 1.1
        ? "warn"
        : "bad";

// ──────────────────────────────────────────────────────────────────────
// Agregados
// ──────────────────────────────────────────────────────────────────────

export interface Totals {
  lines: number;
  /** Facturación prevista y real. */
  plannedSale: number;
  sale: number;
  plannedCost: number;
  realCost: number;
  plannedProfit: number;
  profit: number;
  /** Facturación de las líneas con margen (base de los % de margen). */
  plannedSaleWithMargin: number;
  saleWithMargin: number;
  /** Facturación de compra de medios. */
  saleCM: number;
  salePM: number;
  /** Beneficio que daría lo facturado con el margen vendido. */
  soldProfit: number;
  /** Facturación de las líneas con margen vendido. */
  saleWithSoldMargin: number;
  /** Beneficio real de esas mismas líneas. */
  profitWithSoldMargin: number;
  plannedFee: number;
  fee: number;
  /** Ratios. */
  compliance: number;
  profitCompliance: number;
  plannedMargin: number;
  margin: number;
  soldMargin: number;
  /** Margen del margen en euros. */
  marginOverSold: number;
}

export function aggregate(rows: ControlBoardRow[]): Totals {
  const t = {
    lines: rows.length,
    plannedSale: 0,
    sale: 0,
    plannedCost: 0,
    realCost: 0,
    plannedSaleWithMargin: 0,
    saleWithMargin: 0,
    saleCM: 0,
    salePM: 0,
    soldProfit: 0,
    saleWithSoldMargin: 0,
    profitWithSoldMargin: 0,
    plannedFee: 0,
    fee: 0,
  };
  rows.forEach((row) => {
    t.plannedSale += row.plannedSale;
    t.sale += row.sale;
    t.plannedCost += row.plannedPlatformCost;
    t.realCost += row.realCost;
    if (row.marginApplies) {
      t.plannedSaleWithMargin += row.plannedSale;
      t.saleWithMargin += row.sale;
    }
    if (row.type === "CM") t.saleCM += row.sale;
    else t.salePM += row.sale;
    if (row.soldMargin !== null && row.sale > 0) {
      t.soldProfit += row.sale * row.soldMargin;
      t.saleWithSoldMargin += row.sale;
      t.profitWithSoldMargin += row.sale - row.realCost;
    }
    t.plannedFee += (row.plannedSale * row.feePct) / 100;
    t.fee += (row.sale * row.feePct) / 100;
  });
  const plannedProfit = t.plannedSale - t.plannedCost;
  const profit = t.sale - t.realCost;
  return {
    ...t,
    plannedProfit,
    profit,
    compliance: t.plannedSale ? t.sale / t.plannedSale : NaN,
    profitCompliance: plannedProfit ? profit / plannedProfit : NaN,
    plannedMargin: t.plannedSaleWithMargin
      ? plannedProfit / t.plannedSaleWithMargin
      : NaN,
    margin: t.saleWithMargin ? profit / t.saleWithMargin : NaN,
    soldMargin: t.saleWithSoldMargin
      ? t.soldProfit / t.saleWithSoldMargin
      : NaN,
    marginOverSold: t.profitWithSoldMargin - t.soldProfit,
  };
}

export function groupBy<T, K>(items: T[], key: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>();
  items.forEach((item) => {
    const k = key(item);
    const list = map.get(k);
    if (list) list.push(item);
    else map.set(k, [item]);
  });
  return map;
}

export const sum = <T>(items: T[], value: (item: T) => number): number =>
  items.reduce((total, item) => total + value(item), 0);

export const mean = (values: number[]): number => {
  const valid = values.filter((v) => finite(v) && v > 0);
  return valid.length ? sum(valid, (v) => v) / valid.length : NaN;
};

export function percentile(values: number[], p: number): number {
  const sorted = values.filter((v) => finite(v) && v > 0).sort((a, b) => a - b);
  if (!sorted.length) return NaN;
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.round(p * (sorted.length - 1)))
  );
  return sorted[index];
}

export const median = (values: number[]) => percentile(values, 0.5);

/** Nombres de una lista de personas, o "Sin asignar". */
export const peopleLabel = (people: string[]): string =>
  people.length ? people.join(", ") : "Sin asignar";

/** Claves por persona: una fila cuenta para cada técnico/account que la lleva. */
export function byPerson(
  rows: ControlBoardRow[],
  people: (row: ControlBoardRow) => string[]
): Map<string, ControlBoardRow[]> {
  const map = new Map<string, ControlBoardRow[]>();
  rows.forEach((row) => {
    const names = people(row);
    (names.length ? names : ["Sin asignar"]).forEach((name) => {
      const list = map.get(name);
      if (list) list.push(row);
      else map.set(name, [row]);
    });
  });
  return map;
}

// ──────────────────────────────────────────────────────────────────────
// Costes y precios unitarios
// ──────────────────────────────────────────────────────────────────────

/** Coste real de plataforma por unidad (por mil en CPM). */
export const realUnitCost = (row: ControlBoardRow): number =>
  row.volume > 0 ? row.realCost / (row.volume / row.unitDivisor) : NaN;

export interface UnitStats {
  lines: number;
  sale: number;
  /** Media simple por línea: cada campaña cuenta igual. */
  realUnitCost: number;
  plannedUnitCost: number;
  unitPrice: number;
  p10: number;
  p50: number;
  p90: number;
  /** Volumen entregado frente al previsto. */
  delivery: number;
}

export function unitStats(rows: ControlBoardRow[]): UnitStats {
  const costs = rows.map(realUnitCost);
  const planned = sum(rows, (r) => r.plannedVolume);
  return {
    lines: rows.length,
    sale: sum(rows, (r) => r.sale),
    realUnitCost: mean(costs),
    plannedUnitCost: mean(rows.map((r) => r.netUnitCost)),
    unitPrice: mean(rows.map((r) => r.grossUnitCost)),
    p10: percentile(costs, 0.1),
    p50: percentile(costs, 0.5),
    p90: percentile(costs, 0.9),
    delivery: planned ? sum(rows, (r) => r.volume) / planned : NaN,
  };
}

// ──────────────────────────────────────────────────────────────────────
// Business Plan
// ──────────────────────────────────────────────────────────────────────

export interface PlanTotals {
  investment: number;
  profit: number;
  fee: number;
  investmentCM: number;
}

export function planTotals(lines: ControlBoardPlanLine[]): PlanTotals {
  return {
    investment: sum(lines, (l) => l.managedInvestment),
    profit: sum(lines, (l) => l.campaignProfit),
    fee: sum(lines, (l) => l.fee),
    investmentCM: sum(
      lines.filter((l) => l.type === "CM"),
      (l) => l.managedInvestment
    ),
  };
}

export interface ChannelBridge {
  channel: ControlBoardChannel;
  real: Totals;
  planInvestment: number;
  planProfit: number;
  /** Peso en la inversión real y en el plan. */
  realShare: number;
  planShare: number;
  /** Margen del plan en el canal. */
  planMargin: number;
  /** Lo que suma o resta al beneficio el cambio de peso del canal (account). */
  mix: number;
  /** Diferencia entre el margen vendido y el del plan (precio). */
  price: number;
  /** Margen del margen (técnico). */
  technician: number;
}

export interface Bridge {
  planInvestment: number;
  planProfit: number;
  realInvestment: number;
  realProfit: number;
  /** Efecto de facturar más o menos que el plan con su margen medio. */
  volume: number;
  mix: number;
  price: number;
  technician: number;
  channels: Record<ControlBoardChannel, ChannelBridge>;
  /** Peso de compra de medios en el plan y en lo real. */
  planShareCM: number;
  realShareCM: number;
  /** Efecto en beneficio del cambio de peso entre compra de medios y paid media. */
  typeEffect: number;
}

/**
 * Puente del beneficio del Business Plan al real. Descompone la diferencia en volumen (facturar
 * más o menos), reparto entre canales (lo decide el account), precio (vender con más o menos
 * margen que el plan) y margen del margen (lo que saca el técnico en la compra).
 */
export function bridge(
  rows: ControlBoardRow[],
  plan: ControlBoardPlanLine[]
): Bridge {
  const realByChannel = groupBy(rows, (r) => r.channel);
  const planByChannel = groupBy(plan, (l) => l.channel);
  const real = Object.fromEntries(
    CHANNELS.map((c) => [c, aggregate(realByChannel.get(c) ?? [])])
  ) as Record<ControlBoardChannel, Totals>;
  const planInvestment = sum(plan, (l) => l.managedInvestment);
  const planProfit = sum(plan, (l) => l.campaignProfit);
  const realInvestment = sum(rows, (r) => r.sale);
  const meanPlanMargin = planInvestment ? planProfit / planInvestment : 0;

  const channels = {} as Record<ControlBoardChannel, ChannelBridge>;
  let mix = 0;
  let price = 0;
  let technician = 0;
  CHANNELS.forEach((c) => {
    const lines = planByChannel.get(c) ?? [];
    const pInv = sum(lines, (l) => l.managedInvestment);
    const pProfit = sum(lines, (l) => l.campaignProfit);
    const r = real[c];
    const soldMargin = finite(r.soldMargin) ? r.soldMargin : 0;
    const planMargin = pInv ? pProfit / pInv : soldMargin;
    const realShare = realInvestment ? r.sale / realInvestment : 0;
    const planShare = planInvestment ? pInv / planInvestment : 0;
    const channelMix =
      (realShare - planShare) * realInvestment * (planMargin - meanPlanMargin);
    const channelPrice = r.saleWithSoldMargin * (soldMargin - planMargin);
    channels[c] = {
      channel: c,
      real: r,
      planInvestment: pInv,
      planProfit: pProfit,
      realShare,
      planShare,
      planMargin: pInv ? pProfit / pInv : NaN,
      mix: channelMix,
      price: channelPrice,
      technician: r.marginOverSold,
    };
    mix += channelMix;
    price += channelPrice;
    technician += r.marginOverSold;
  });

  const planCM = sum(
    plan.filter((l) => l.type === "CM"),
    (l) => l.managedInvestment
  );
  const planProfitCM = sum(
    plan.filter((l) => l.type === "CM"),
    (l) => l.campaignProfit
  );
  const realCM = sum(
    rows.filter((r) => r.type === "CM"),
    (r) => r.sale
  );
  const planShareCM = planInvestment ? planCM / planInvestment : NaN;
  const realShareCM = realInvestment ? realCM / realInvestment : NaN;
  const marginCM = planCM ? planProfitCM / planCM : 0;

  return {
    planInvestment,
    planProfit,
    realInvestment,
    realProfit: sum(rows, (r) => r.sale - r.realCost),
    volume: (realInvestment - planInvestment) * meanPlanMargin,
    mix,
    price,
    technician,
    channels,
    planShareCM,
    realShareCM,
    typeEffect:
      finite(planShareCM) && finite(realShareCM)
        ? (realShareCM - planShareCM) * realInvestment * marginCM
        : 0,
  };
}

/** Filas y plan del periodo: un mes, o el año hasta el último mes con datos. */
export function inPeriod<T extends { month: number }>(
  items: T[],
  month: number,
  lastMonth: number
): T[] {
  return month
    ? items.filter((i) => i.month === month)
    : items.filter((i) => i.month <= lastMonth);
}

export const typeOf = (channel: ControlBoardChannel): BuyType =>
  isPaidMedia(channel) ? "PM" : "CM";
